// Landing page — public marketing front door (Task 4.1 / D-31).
// SSR-safe: no AuthContext, no react-router. Community fetch is client-only (useEffect).

import { useEffect, useState } from "react";
import {
  Briefcase,
  Camera,
  Home,
  LayoutTemplate,
  PartyPopper,
  Plane,
  Repeat,
  Users,
  UsersRound,
} from "lucide-react";
import { AiDemo } from "../components/marketing/AiDemo.js";
import { CtaBand } from "../components/marketing/CtaBand.js";
import { FaqItem } from "../components/marketing/FaqItem.js";
import { Hero } from "../components/marketing/Hero.js";
import { MarketingLayout } from "../components/marketing/MarketingLayout.js";
import { MarketingShot } from "../components/marketing/MarketingShot.js";
import { Section } from "../components/marketing/Section.js";
import { StepCard } from "../components/marketing/StepCard.js";
import { UseCaseCard } from "../components/marketing/UseCaseCard.js";
import { ValueProp } from "../components/marketing/ValueProp.js";
import { AnchorButton } from "../components/ui/Button.js";
import {
  fetchCommunityLists,
  type ApiCommunityListSummary,
} from "../lib/api.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel, signupTagline } from "../lib/signupCopy.js";

function CommunityProofStrip() {
  const [lists, setLists] = useState<ApiCommunityListSummary[] | null>(null);

  useEffect(() => {
    // Client-only: must not run during pre-render (Task 4.1). On failure, render nothing.
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchCommunityLists();
        if (cancelled) return;
        const sorted = [...(res.lists ?? [])].sort(
          (a, b) => (b.copyCount ?? 0) - (a.copyCount ?? 0)
        );
        setLists(sorted.slice(0, 6));
      } catch {
        if (!cancelled) setLists(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!lists || lists.length === 0) return null;

  return (
    <Section tone="slate" id="community">
      <h2 className="text-2xl font-semibold text-ink sm:text-3xl">From the community</h2>
      <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
        Ready-made lists from NeatTick and the community. Open any of them, or start a checklist after
        you sign up.
      </p>
      <ul className="mt-8 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
        {lists.map((list) => (
          <li key={list.id}>
            <a
              href={`/community/${list.id}`}
              className="flex h-full flex-col rounded-2xl border border-line bg-white p-5 no-underline shadow-sm transition hover:border-brand-200 hover:bg-brand-50/40"
            >
              <span className="text-lg font-semibold text-ink">{list.name}</span>
              <span className="mt-2 text-sm text-muted">
                {list.itemCount} items
                {list.copyCount > 0 ? ` · copied ${list.copyCount}×` : ""}
              </span>
              <span className="mt-1 text-sm text-muted-sm">by {list.ownerScreenName}</span>
            </a>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function LandingPage() {
  const { mode: signupMode } = useSignupMode();
  return (
    <MarketingLayout>
      {/* 1 — Hero */}
      <Section tone="surface">
        <Hero
          title="The checklists you do more than once"
          subhead="Stop rewriting the same list every time. Build it once, reuse it forever, share it with anyone, and check it off together."
          actions={
            <>
              <AnchorButton href="/register">{signupLabel("primary", signupMode)}</AnchorButton>
              <AnchorButton href="/community" variant="secondary">
                Browse community lists
              </AnchorButton>
            </>
          }
          media={
            <MarketingShot
              name="hero-phone"
              alt="Phone showing the Cabo, July 2026 packing checklist mid-progress with live attribution chips"
              width={780}
              height={975}
              priority
              className="mx-auto h-auto max-h-[28rem] w-auto max-w-full rounded-3xl border border-line shadow-sm"
            />
          }
        />
        {signupTagline(signupMode) ? (
          <p className="mt-6 text-sm text-muted">{signupTagline(signupMode)}</p>
        ) : null}
      </Section>

      {/* 2 — The problem */}
      <Section tone="amber">
        <p className="mx-auto max-w-3xl text-center text-lg leading-relaxed text-ink sm:text-xl">
          You&apos;ve packed for this trip before. You&apos;ve onboarded someone before. You&apos;ve
          run this event before.
        </p>
        <p className="mx-auto mt-4 max-w-3xl text-center text-xl font-semibold text-ink sm:text-2xl">
          So why are you starting from a blank page again?
        </p>
      </Section>

      {/* 3 — Three value propositions */}
      <Section tone="surface">
        <h2 className="sr-only">What you get</h2>
        <div className="grid gap-6 sm:grid-cols-3">
          <ValueProp
            icon={<Repeat size={20} aria-hidden="true" />}
            title="Build it once"
            body={
              'Make a list of what "camping" actually needs. Next trip, it\'s already there — nothing forgotten because you were packing at 11pm.'
            }
          />
          <ValueProp
            icon={<LayoutTemplate size={20} aria-hidden="true" />}
            title="Combine and reuse"
            body={
              'Snap your saved lists together into a template. "Beach trip" = swim bag + toiletries + kids\' stuff. One click turns it into a fresh checklist.'
            }
          />
          <ValueProp
            icon={<Users size={20} aria-hidden="true" />}
            title="Check it off together"
            body={
              'Share with anyone. Everyone sees checkmarks appear live, and you can see who checked what. No more "did you grab the sunscreen?"'
            }
          />
        </div>
      </Section>

      {/* 4 — How it works (condensed) */}
      <Section tone="slate" id="how-it-works-preview">
        <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
          Up and running in about a minute
        </h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          <StepCard
            step={1}
            title="Start a checklist."
            body="Type a name, add items — or describe what you're doing and let AI build it. Copy one from the community if someone's already made it."
          />
          <StepCard
            step={2}
            title="Check things off."
            body="On your phone, as you go. Share the link and anyone can check items with you, live."
          />
          <StepCard
            step={3}
            title="Save it for next time."
            body="Turn any checklist into a reusable template with one click. Next time, you start from done instead of from blank."
          />
        </div>
        <p className="mt-8">
          <a
            href="/how-it-works"
            className="text-base font-semibold text-brand-700 no-underline hover:text-brand-600"
          >
            See the full walkthrough →
          </a>
        </p>
      </Section>

      {/* 5 — Use cases */}
      <Section tone="surface" id="use-cases">
        <h2 className="text-2xl font-semibold text-ink sm:text-3xl">What people use it for</h2>
        <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
          Anything you do more than once.
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <UseCaseCard
            icon={<Plane size={20} aria-hidden="true" />}
            title="Travel & trips"
            body="Carry-on essentials · Camping gear · Beach day with kids · International travel docs"
            href="/packing-list"
          />
          <UseCaseCard
            icon={<Home size={20} aria-hidden="true" />}
            title="Home & life"
            body="Moving house · Weekly reset · Emergency go-bag · Seasonal switchover"
            href="/community?category=home"
          />
          <UseCaseCard
            icon={<UsersRound size={20} aria-hidden="true" />}
            title="Kids & family"
            body="School year prep · Sports bag · Sleepover · Newborn hospital bag"
            href="/community?category=family"
          />
          <UseCaseCard
            icon={<Briefcase size={20} aria-hidden="true" />}
            title="Work & process"
            body="New hire onboarding · Client kickoff · Website launch · Month-end close"
            href="/onboarding-checklist"
          />
          <UseCaseCard
            icon={<PartyPopper size={20} aria-hidden="true" />}
            title="Events"
            body="Dinner party · Wedding day-of · Camping trip with friends · Race day"
            href="/wedding-checklist"
          />
          <UseCaseCard
            icon={<Camera size={20} aria-hidden="true" />}
            title="Hobbies"
            body="Photography kit · Fishing tackle · Gig setup · Hiking day pack"
            href="/community?category=travel"
          />
        </div>
      </Section>

      {/* 6 — Live community proof (client-only fetch; silent on failure) */}
      <CommunityProofStrip />

      {/* 7 — Sharing / live sync spotlight */}
      <Section tone="surface" id="sharing">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
              Everyone&apos;s looking at the same list
            </h2>
            <p className="mt-4 max-w-[65ch] text-base leading-7 text-muted">
              Share a checklist with a link. When two or more people are on it, checkmarks sync live
              — you see items get checked the moment someone checks them, and who did it. Give
              people view-only access or let them check things off.
            </p>
            <p className="mt-4">
              <a
                href="/shared-checklist"
                className="text-base font-semibold text-brand-700 no-underline hover:text-brand-600"
              >
                How shared checklists work →
              </a>
            </p>
          </div>
          <MarketingShot
            name="live-sync-phones"
            alt="Two phones side by side showing the same shared checklist updating live with You and Alex attribution chips"
            width={1280}
            height={800}
            className="h-auto w-full rounded-3xl border border-line shadow-sm"
          />
        </div>
      </Section>

      {/* 8 — AI spotlight (+ Task 4.3 demo widget slot) */}
      <Section tone="amber" id="ai">
        <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
          Don&apos;t know where to start? Describe it.
        </h2>
        <p className="mt-4 max-w-[65ch] text-base leading-7 text-muted">
          &ldquo;Weekend ski trip with two toddlers.&rdquo; &ldquo;Onboarding a remote
          developer.&rdquo; &ldquo;Three-day music festival.&rdquo;
        </p>
        <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
          You get a real, sensible checklist in seconds — then edit it however you like.
        </p>
        <AiDemo />
      </Section>

      {/* 9 — FAQ */}
      <Section tone="surface" id="faq">
        <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Questions, answered</h2>
        <div className="mx-auto mt-8 max-w-2xl space-y-3">
          <FaqItem question="Is it free?" answer="Yes. Free to use, no credit card." />
          <FaqItem
            question="What's the difference between a list, a template, and a checklist?"
            answer={
              <p>
                A <strong className="font-semibold text-ink">checklist</strong> is the thing you
                check off. A <strong className="font-semibold text-ink">template</strong> is a
                reusable blueprint for making that checklist again. A{" "}
                <strong className="font-semibold text-ink">list</strong> is a building block you can
                drop into templates. Most people start with a checklist and graduate into the rest.{" "}
                <a href="/how-it-works" className="font-semibold text-brand-700 no-underline">
                  See how it works →
                </a>
              </p>
            }
          />
          <FaqItem
            question="Do the people I share with need an account?"
            answer="Yes, so we can show who checked what — but signup takes seconds."
          />
          <FaqItem
            question="Does it work on my phone?"
            answer="It's built for phones first. That's where you actually check things off."
          />
          <FaqItem
            question="Can I use it for work things, not just packing?"
            answer="Yes — any process you repeat."
          />
          <FaqItem
            question="What happens to my checklist if I edit the original list?"
            answer="Nothing. Each checklist is a snapshot. Editing the source lists or template never changes checklists you've already started."
          />
        </div>
      </Section>

      {/* 10 — Closing CTA */}
      <Section tone="slate">
        <CtaBand
          title="Your next trip, project, or event is already half-planned."
          body={signupTagline(signupMode) ?? "Sign-up is closed right now."}
          primaryHref="/register"
          primaryLabel={signupLabel("primary", signupMode)}
          secondaryHref="/how-it-works"
          secondaryLabel="See how it works"
        />
      </Section>
    </MarketingLayout>
  );
}
